// src/lib/charts/colors.ts

import type { 
    ChartColor, 
    ColorUtility, 
    ChartColorUtils, 
    ColorConfig,
    OpacityLevel,
  } from './types'
  
/**
 * Chart color configuration object defining Tailwind classes for different utilities.
 * Includes dark mode variants.
 * NOTE: CONTENT key removed as contrast should be handled by context (e.g., tooltip background, site background).
 * @constant
 */
export const chartColors = {
  blue: {
    DEFAULT: "bg-blue-500 dark:bg-blue-500",
    stroke: "stroke-blue-500 dark:stroke-blue-500", 
    fill: "fill-blue-500 dark:fill-blue-500",
    text: "text-blue-500 dark:text-blue-500",
    border: "border-blue-500 dark:border-blue-500",
    hover: "hover:bg-blue-600 dark:hover:bg-blue-400",
    gradient: "from-blue-500 to-blue-400 dark:from-blue-500 dark:to-blue-400",
    SUBTLE: "bg-blue-100 dark:bg-blue-900",
    EMPHASIS: "bg-blue-700 dark:bg-blue-300",
    opacity: "bg-blue-500/50 dark:bg-blue-500/50",
  },
  indigo: {
    DEFAULT: "bg-indigo-500 dark:bg-indigo-500",
    stroke: "stroke-indigo-500 dark:stroke-indigo-500",
    fill: "fill-indigo-500 dark:fill-indigo-500",
    text: "text-indigo-500 dark:text-indigo-500", 
    border: "border-indigo-500 dark:border-indigo-500",
    hover: "hover:bg-indigo-600 dark:hover:bg-indigo-400",
    gradient: "from-indigo-500 to-indigo-400 dark:from-indigo-500 dark:to-indigo-400",
    SUBTLE: "bg-indigo-100 dark:bg-indigo-900",
    EMPHASIS: "bg-indigo-700 dark:bg-indigo-300",
    opacity: "bg-indigo-500/50 dark:bg-indigo-500/50",
  },
  emerald: {
    DEFAULT: "bg-emerald-500 dark:bg-emerald-500",
    stroke: "stroke-emerald-500 dark:stroke-emerald-500",
    fill: "fill-emerald-500 dark:fill-emerald-500", 
    text: "text-emerald-500 dark:text-emerald-500",
    border: "border-emerald-500 dark:border-emerald-500",
    hover: "hover:bg-emerald-600 dark:hover:bg-emerald-400",
    gradient: "from-emerald-500 to-emerald-400 dark:from-emerald-500 dark:to-emerald-400",
    SUBTLE: "bg-emerald-100 dark:bg-emerald-900",
    EMPHASIS: "bg-emerald-700 dark:bg-emerald-300",
    opacity: "bg-emerald-500/50 dark:bg-emerald-500/50",
  },
  violet: {
    DEFAULT: "bg-violet-500 dark:bg-violet-500",
    stroke: "stroke-violet-500 dark:stroke-violet-500",
    fill: "fill-violet-500 dark:fill-violet-500",
    text: "text-violet-500 dark:text-violet-500",
    border: "border-violet-500 dark:border-violet-500",
    hover: "hover:bg-violet-600 dark:hover:bg-violet-400",
    gradient: "from-violet-500 to-violet-400 dark:from-violet-500 dark:to-violet-400",
    SUBTLE: "bg-violet-100 dark:bg-violet-900",
    EMPHASIS: "bg-violet-700 dark:bg-violet-300",
    opacity: "bg-violet-500/50 dark:bg-violet-500/50",
  },
  orange: {
    DEFAULT: "bg-orange-500 dark:bg-orange-500",
    stroke: "stroke-orange-500 dark:stroke-orange-500",
    fill: "fill-orange-500 dark:fill-orange-500",
    text: "text-orange-500 dark:text-orange-500",
    border: "border-orange-500 dark:border-orange-500",
    hover: "hover:bg-orange-600 dark:hover:bg-orange-400",
    gradient: "from-orange-500 to-orange-400 dark:from-orange-500 dark:to-orange-400",
    SUBTLE: "bg-orange-100 dark:bg-orange-900",
    EMPHASIS: "bg-orange-700 dark:bg-orange-300",
    opacity: "bg-orange-500/50 dark:bg-orange-500/50",
  },
  amber: {
    DEFAULT: "bg-amber-500 dark:bg-amber-500", 
    stroke: "stroke-amber-500 dark:stroke-amber-500",
    fill: "fill-amber-500 dark:fill-amber-500",
    text: "text-amber-500 dark:text-amber-500",
    border: "border-amber-500 dark:border-amber-500",
    hover: "hover:bg-amber-600 dark:hover:bg-amber-400",
    gradient: "from-amber-500 to-amber-400 dark:from-amber-500 dark:to-amber-400",
    SUBTLE: "bg-amber-100 dark:bg-amber-900",
    EMPHASIS: "bg-amber-700 dark:bg-amber-300",
    opacity: "bg-amber-500/50 dark:bg-amber-500/50",
  },
  teal: {
    DEFAULT: "bg-teal-500 dark:bg-teal-500",
    stroke: "stroke-teal-500 dark:stroke-teal-500",
    fill: "fill-teal-500 dark:fill-teal-500",
    text: "text-teal-500 dark:text-teal-500",
    border: "border-teal-500 dark:border-teal-500",
    hover: "hover:bg-teal-600 dark:hover:bg-teal-400",
    gradient: "from-teal-500 to-teal-400 dark:from-teal-500 dark:to-teal-400",
    SUBTLE: "bg-teal-100 dark:bg-teal-900",
    EMPHASIS: "bg-teal-700 dark:bg-teal-300",
    opacity: "bg-teal-500/50 dark:bg-teal-500/50",
  },
  rose: {
    DEFAULT: "bg-rose-500 dark:bg-rose-500",
    stroke: "stroke-rose-500 dark:stroke-rose-500",
    fill: "fill-rose-500 dark:fill-rose-500",
    text: "text-rose-500 dark:text-rose-500",
    border: "border-rose-500 dark:border-rose-500",
    hover: "hover:bg-rose-600 dark:hover:bg-rose-400",
    gradient: "from-rose-500 to-rose-400 dark:from-rose-500 dark:to-rose-400",
    SUBTLE: "bg-rose-100 dark:bg-rose-900",
    EMPHASIS: "bg-rose-700 dark:bg-rose-300",
    opacity: "bg-rose-500/50 dark:bg-rose-500/50",
  },
  gray: {
    DEFAULT: "bg-gray-500 dark:bg-gray-500",
    stroke: "stroke-gray-500 dark:stroke-gray-500",
    fill: "fill-gray-500 dark:fill-gray-500",
    text: "text-gray-500 dark:text-gray-500",
    border: "border-gray-500 dark:border-gray-500",
    hover: "hover:bg-gray-600 dark:hover:bg-gray-400",
    gradient: "from-gray-500 to-gray-400 dark:from-gray-500 dark:to-gray-400",
    SUBTLE: "bg-gray-200 dark:bg-gray-700",
    EMPHASIS: "bg-gray-700 dark:bg-gray-300",
    opacity: "bg-gray-500/50 dark:bg-gray-400/50",
  },
  cyan: {
    DEFAULT: "bg-cyan-500 dark:bg-cyan-400",
    stroke: "stroke-cyan-500 dark:stroke-cyan-400",
    fill: "fill-cyan-500 dark:fill-cyan-400",
    text: "text-cyan-500 dark:text-cyan-400",
    border: "border-cyan-500 dark:border-cyan-400",
    hover: "hover:bg-cyan-600 dark:hover:bg-cyan-300",
    gradient: "from-cyan-500 to-cyan-400 dark:from-cyan-400 dark:to-cyan-300",
    SUBTLE: "bg-cyan-100 dark:bg-cyan-800",
    EMPHASIS: "bg-cyan-700 dark:bg-cyan-200",
    opacity: "bg-cyan-500/50 dark:bg-cyan-400/50",
  },
  pink: {
    DEFAULT: "bg-pink-500 dark:bg-pink-400",
    stroke: "stroke-pink-500 dark:stroke-pink-400",
    fill: "fill-pink-500 dark:fill-pink-400",
    text: "text-pink-500 dark:text-pink-400",
    border: "border-pink-500 dark:border-pink-400",
    hover: "hover:bg-pink-600 dark:hover:bg-pink-300",
    gradient: "from-pink-500 to-pink-400 dark:from-pink-400 dark:to-pink-300",
    SUBTLE: "bg-pink-100 dark:bg-pink-800",
    EMPHASIS: "bg-pink-700 dark:bg-pink-200",
    opacity: "bg-pink-500/50 dark:bg-pink-400/50",
  },
  lime: {
     DEFAULT: "bg-lime-500 dark:bg-lime-400",
     stroke: "stroke-lime-500 dark:stroke-lime-400",
     fill: "fill-lime-500 dark:fill-lime-400",
     text: "text-lime-500 dark:text-lime-400",
     border: "border-lime-500 dark:border-lime-400",
     hover: "hover:bg-lime-600 dark:hover:bg-lime-300",
     gradient: "from-lime-500 to-lime-400 dark:from-lime-400 dark:to-lime-300",
     SUBTLE: "bg-lime-100 dark:bg-lime-800",
     EMPHASIS: "bg-lime-700 dark:bg-lime-200",
     opacity: "bg-lime-500/50 dark:bg-lime-400/50",
  },
  fuchsia: {
     DEFAULT: "bg-fuchsia-500 dark:bg-fuchsia-400",
     stroke: "stroke-fuchsia-500 dark:stroke-fuchsia-400",
     fill: "fill-fuchsia-500 dark:fill-fuchsia-400",
     text: "text-fuchsia-500 dark:text-fuchsia-400",
     border: "border-fuchsia-500 dark:border-fuchsia-400",
     hover: "hover:bg-fuchsia-600 dark:hover:bg-fuchsia-300",
     gradient: "from-fuchsia-500 to-fuchsia-400 dark:from-fuchsia-400 dark:to-fuchsia-300",
     SUBTLE: "bg-fuchsia-100 dark:bg-fuchsia-800",
     EMPHASIS: "bg-fuchsia-700 dark:bg-fuchsia-200",
     opacity: "bg-fuchsia-500/50 dark:bg-fuchsia-400/50",
  },
  sky: {
    DEFAULT: "bg-sky-500 dark:bg-sky-400",
    stroke: "stroke-sky-500 dark:stroke-sky-400",
    fill: "fill-sky-500 dark:fill-sky-400",
    text: "text-sky-500 dark:text-sky-400",
    border: "border-sky-500 dark:border-sky-400",
    hover: "hover:bg-sky-600 dark:hover:bg-sky-300",
    gradient: "from-sky-500 to-sky-400 dark:from-sky-400 dark:to-sky-300",
    SUBTLE: "bg-sky-100 dark:bg-sky-800",
    EMPHASIS: "bg-sky-700 dark:bg-sky-200",
    opacity: "bg-sky-500/50 dark:bg-sky-400/50",
  },
  purple: {
    DEFAULT: "bg-purple-500 dark:bg-purple-400",
    stroke: "stroke-purple-500 dark:stroke-purple-400",
    fill: "fill-purple-500 dark:fill-purple-400",
    text: "text-purple-500 dark:text-purple-400",
    border: "border-purple-500 dark:border-purple-400",
    hover: "hover:bg-purple-600 dark:hover:bg-purple-300",
    gradient: "from-purple-500 to-purple-400 dark:from-purple-400 dark:to-purple-300",
    SUBTLE: "bg-purple-100 dark:bg-purple-800",
    EMPHASIS: "bg-purple-700 dark:bg-purple-200",
    opacity: "bg-purple-500/50 dark:bg-purple-400/50",
  },
  white: {
    DEFAULT: "bg-white dark:bg-gray-100",
    stroke: "stroke-gray-900 dark:stroke-gray-100",
    fill: "fill-white dark:fill-gray-100",
    text: "text-gray-900 dark:text-gray-100",
    border: "border-gray-200 dark:border-gray-100",
    hover: "hover:bg-gray-100 dark:hover:bg-white",
    gradient: "from-white to-gray-50 dark:from-gray-100 dark:to-white",
    SUBTLE: "bg-gray-50 dark:bg-gray-200",
    EMPHASIS: "bg-gray-200 dark:bg-white",
    opacity: "bg-white/50 dark:bg-gray-100/50",
  },
  red: {
    DEFAULT: "bg-red-500 dark:bg-red-400",
    stroke: "stroke-red-500 dark:stroke-red-400",
    fill: "fill-red-500 dark:fill-red-400",
    text: "text-red-500 dark:text-red-400",
    border: "border-red-500 dark:border-red-400",
    hover: "hover:bg-red-600 dark:hover:bg-red-300",
    gradient: "from-red-500 to-red-400 dark:from-red-400 dark:to-red-300",
    SUBTLE: "bg-red-100 dark:bg-red-800",
    EMPHASIS: "bg-red-700 dark:bg-red-200",
    opacity: "bg-red-500/50 dark:bg-red-400/50",
  },
  value: {
    DEFAULT: "bg-gray-500 dark:bg-gray-400",
    stroke: "stroke-gray-500 dark:stroke-gray-400",
    fill: "fill-gray-500 dark:fill-gray-400",
    text: "text-gray-500 dark:text-gray-400",
    border: "border-gray-500 dark:border-gray-400",
    hover: "hover:bg-gray-600 dark:hover:bg-gray-300",
    gradient: "from-gray-500 to-gray-400 dark:from-gray-400 dark:to-gray-300",
    SUBTLE: "bg-gray-100 dark:bg-gray-700",
    EMPHASIS: "bg-gray-700 dark:bg-gray-300",
    opacity: "bg-gray-500/50 dark:bg-gray-400/50",
  }
}
  
/**
 * Array of available chart colors for use in color cycling and validation
 * Ensures consistency across the application
 */
export const AvailableChartColors: ReadonlyArray<ChartColor> = Object.freeze(
  Object.keys(chartColors).filter(key => key !== 'value') as ChartColor[]
)
  
// --- Predefined Palettes ---
  
/** Default palette for categorical data with good visual distinction */
// --- Palettes ---
export const DEFAULT_CATEGORICAL_PALETTE: readonly ChartColor[] = Object.freeze([
  'sky', 'amber', 'emerald', 'rose', 'indigo', 'lime', 'fuchsia', 'cyan', 'orange', 'teal'
])
  
/** Sequential palettes for different color schemes */
export const SEQUENTIAL_PALETTES: Readonly<Record<string, readonly ChartColor[]>> = Object.freeze({
  blue: ['sky', 'cyan', 'blue', 'indigo', 'violet'] as const,
  emerald: ['emerald', 'lime', 'teal', 'cyan'] as const,
  purple: ['purple', 'fuchsia', 'pink', 'rose', 'violet'] as const,
  warm: ['orange', 'amber', 'lime', 'emerald'] as const,
  cool: ['blue', 'indigo', 'violet', 'purple', 'cyan'] as const,
  // Add other sequential palettes if needed
})
  
// Define a Diverging Palette
const DIVERGING_PALETTE: readonly ChartColor[] = Object.freeze([
  'red', 'orange', 'amber', 'white', 'lime', 'emerald', 'cyan', 'blue' // Example Red-White-Blue
])
  
/** Fallback color if no other color is available or specified */
export const FALLBACK_COLOR: ChartColor = 'blue' // Changed to blue as it's guaranteed to exist and is visually neutral
  
// --- Internal Hex Color Mapping ---
// Maps ChartColor names to standard Tailwind 500 hex codes
// Kept internal to this module, exposed via getHexColor utility
const _colorNameToHex: Readonly<Record<ChartColor, string>> = Object.freeze({
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  purple: '#a855f7',
  fuchsia: '#d946ef',
  pink: '#ec4899',
  rose: '#f43f5e',
  red: '#ef4444',
  orange: '#f97316',
  amber: '#f59e0b',
  lime: '#84cc16',
  emerald: '#10b981',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  gray: '#6b7280',
  white: '#ffffff',
  value: '#6b7280', // Map 'value' to gray hex as required by type
})


/**
 * Utility functions for working with chart colors and styling
 * Provides consistent color handling across different chart types
 * with proper dark mode support
 */
export const chartColorUtils: ChartColorUtils = {
    /**
     * Gets the Tailwind class name for a color and utility type
     */
    getClassName: (color: ChartColor | undefined, type: ColorUtility, opacity?: OpacityLevel): string => {
      const safeColor = color ?? FALLBACK_COLOR
      const colorDefinition = chartColors[safeColor] ?? chartColors[FALLBACK_COLOR]
      // Use type assertion to ensure TypeScript knows the property exists
      const baseClass = colorDefinition[type as keyof typeof colorDefinition] ?? colorDefinition.DEFAULT
      
      // Handle opacity modification
      if (opacity !== undefined && opacity >= 0 && opacity <= 100) {
        const lightClass = baseClass.split(' ')[0] ?? ''
        const darkClass = baseClass.split(' ')[1] ?? ''
        // Attempt to find the base color name (e.g., 'blue-500') to apply opacity
        const lightRegex = /(?:bg|stroke|fill|text|border)-([a-z]+(?:-[0-9]+)?)/
        const darkRegex = /(?:bg|stroke|fill|text|border)-([a-z]+(?:-[0-9]+)?)/
        const lightMatch = lightRegex.exec(lightClass)
        const darkMatch = darkRegex.exec(darkClass)
        
        if (lightMatch?.[0] && darkMatch?.[0]) {
           // Construct class with opacity for both light and dark variants
           const lightBase = lightMatch[0]
           const darkBase = darkMatch[0].startsWith('dark:') ? darkMatch[0].replace(/^dark:/, '') : darkMatch[0]
           // Handle edge case where dark mode class might not exist (though unlikely with our structure)
           if (darkBase) {
             return `${lightBase}/${opacity} dark:${darkBase}/${opacity}`
           } else {
             return `${lightBase}/${opacity}` // Apply only to light if dark somehow missing
           }
        } 
        // Fallback if regex fails: Apply opacity to the whole string
        return `${baseClass}/${opacity}`
      }
      
      return baseClass
    },
    
    /**
     * Ensures a color value exists, defaulting to gray if undefined
     * @param color - Chart color to check
     * @returns Valid chart color, never undefined
     * @example
     * ```ts
     * ensureColor(undefined) // Returns 'gray'
     * ensureColor('blue') // Returns 'blue'
     * ```
     */
    ensureColor: (color: ChartColor | undefined): ChartColor => 
      color ?? FALLBACK_COLOR,
    
    /**
     * Gets the next color in sequence for multiple series
     * Cycles through available colors if index exceeds array length
     * @param index - Current index in sequence
     * @param colors - Array of available colors
     * @returns Next chart color in sequence
     * @example
     * ```ts
     * getNextColor(0, ['blue', 'red']) // Returns 'blue'
     * getNextColor(2, ['blue', 'red']) // Returns 'blue' (cycles)
     * ```
     */
    getNextColor: (index: number, colors: readonly ChartColor[]): ChartColor => {
      if (colors.length === 0) return FALLBACK_COLOR
      // Ensure positive modulo for negative indices
      const safeIndex = ((index % colors.length) + colors.length) % colors.length
      // We know this is safe because we checked length above
      const color = colors[safeIndex]
      return color ?? FALLBACK_COLOR
    },
  
    /**
     * Retrieves the hex color code for a given ChartColor name.
     * @param {ChartColor} colorName - The name of the color (e.g., 'blue', 'emerald').
     * @returns {string} The corresponding hex color code (e.g., '#3b82f6'). Defaults to gray hex if name not found.
     * @example
     * getHexColor('emerald') // returns '#10b981'
     * getHexColor('invalidName' as ChartColor) // returns '#6b7280' (gray hex)
     */
    getHexColor: (colorName: ChartColor | undefined): string => {
      const safeColor = colorName ?? FALLBACK_COLOR
      return _colorNameToHex[safeColor] ?? _colorNameToHex[FALLBACK_COLOR]
    },
  
    /**
     * Generates a color palette (array of ChartColor names) for a specified number of items.
     * Cycles through predefined palettes (categorical by default).
     * @param {number} count - The desired number of colors in the palette.
     * @param {'categorical' | 'sequential' | 'diverging'} [paletteType='categorical'] - The type of palette to use.
     * @returns {ChartColor[]} An array of ChartColor names. Returns empty array if count <= 0.
     * @example
     * getPalette(5) // returns ['sky', 'amber', 'emerald', 'rose', 'indigo']
     * getPalette(3, 'sequential') // returns ['blue', 'blue', 'blue'] (based on current simple sequential example)
     * getPalette(0) // returns ['gray']
     */
    getPalette: (count: number, paletteType: 'categorical' | 'sequential' | 'diverging' = 'categorical'): ChartColor[] => {
      const numColors = Math.max(0, count)
      if (numColors === 0) return []
      
      let sourcePalette: readonly ChartColor[] = DEFAULT_CATEGORICAL_PALETTE
  
      switch(paletteType) {
        case 'sequential':
          sourcePalette = SEQUENTIAL_PALETTES.blue ?? DEFAULT_CATEGORICAL_PALETTE
          break
        case 'diverging':
          sourcePalette = DIVERGING_PALETTE
          break
        case 'categorical':
          sourcePalette = DEFAULT_CATEGORICAL_PALETTE
          break
      }
  
      // No need for ts-ignore as we've handled the undefined case
      if (sourcePalette.length === 0) {
         if (process.env.NODE_ENV === 'development') {
           console.warn(`ChartColorUtils: Palette type '${paletteType}' resulted in an empty source palette; using fallback color`)
         }
         return Array(numColors).fill(FALLBACK_COLOR) as ChartColor[]
      }
  
      // Since we checked length above and sourcePalette is readonly ChartColor[],
      // we can safely assert the array access will return ChartColor
      return Array.from({ length: numColors }, (_, i): ChartColor => {
        const color = sourcePalette[i % sourcePalette.length]
        return color ?? FALLBACK_COLOR
      })
    },
  
    /**
     * Gets the SVG gradient URL identifier for a given color name.
     * Assumes SVG <defs> with IDs like "gradient-blue", "gradient-emerald" are defined elsewhere.
     * @param {ChartColor} colorName - The name of the color.
     * @returns {string} The URL identifier string (e.g., "url(#gradient-blue)").
     * @example
     * getGradientUrl('blue') // returns "url(#gradient-blue)"
     */
    getGradientUrl: (colorName: ChartColor | undefined): string => {
      const safeColor = colorName ?? FALLBACK_COLOR
      return `url(#gradient-${safeColor})`
    },
  
    /**
     * Creates a gradient definition string with dark mode support
     */
    createGradient: (config: ColorConfig): string => {
      const { color, gradient } = config
      const baseColorDef = chartColors[color]
      if (gradient) { 
        // Use explicit gradient config if provided
        return `linear-gradient(${gradient.angle ?? 180}deg, ${gradient.from} ${gradient.stops?.[0]?.offset ?? 0}%, ${gradient.to} ${gradient.stops?.[1]?.offset ?? 100}%)`
      } else {
        // Default gradient using DEFAULT and SUBTLE for a softer look
        const defaultColor = baseColorDef.DEFAULT.split(' ')[0]?.replace('bg-', '') ?? 'gray-500' // Get light mode color
        const subtleColor = baseColorDef.SUBTLE.split(' ')[0]?.replace('bg-', '') ?? 'gray-100' // Get light mode color
        // Using CSS variables that Tailwind might populate, otherwise fallback
        return `linear-gradient(180deg, var(--tw-gradient-from, theme(colors.${defaultColor})) 0%, var(--tw-gradient-to, theme(colors.${subtleColor})) 100%)`
      }
    },
  
    /**
     * Gets color with specified opacity and dark mode support
     */
    getColorWithOpacity: (color: ChartColor, opacityLevel: OpacityLevel): string => {
       // Delegate to getClassName which now handles opacity
       return chartColorUtils.getClassName(color, 'DEFAULT', opacityLevel) 
    },
  
    /**
     * Gets state-specific color class with dark mode support
     */
    getStateColor: (config: ColorConfig, state: 'hover' | 'active' | 'disabled' | 'focus'): string => {
      const baseColorDef = chartColors[config.color]
      switch(state) {
        case 'hover':
          return baseColorDef.hover // Use the specific hover class
        case 'active':
          return baseColorDef.EMPHASIS // Use EMPHASIS for active state
        case 'disabled':
          return baseColorDef.opacity // Use opacity for disabled state
        case 'focus':
          return baseColorDef.SUBTLE // Use SUBTLE for focus state (often a background change)
        default:
          return baseColorDef.DEFAULT // Default to standard representation
      }
    },
  
    /**
     * Creates a color scheme for data visualization with dark mode support
     */
    createColorScheme: (baseColor: ChartColor) => {
      const colorDef = chartColors[baseColor]
      return {
        primary: colorDef.DEFAULT,
        secondary: colorDef.SUBTLE, // Map secondary to SUBTLE
        accent: colorDef.EMPHASIS,  // Map accent to EMPHASIS
        text: colorDef.text,
        border: colorDef.border,
        highlight: colorDef.hover
      }
    },
  
    /**
     * Generates complementary colors for contrasting elements
     * @param color - Base color to find complements for
     * @returns Array of complementary colors
     */
    getComplementaryColors: (color: ChartColor): ChartColor[] => {
      const complementMap: Record<ChartColor, ChartColor[]> = {
        blue: ['indigo', 'orange'],
        indigo: ['emerald', 'orange'],
        emerald: ['violet', 'purple'],
        violet: ['emerald', 'orange'],
        orange: ['blue', 'cyan'],
        amber: ['blue', 'purple'],
        teal: ['violet', 'rose'],
        rose: ['teal', 'cyan'],
        gray: ['blue', 'purple'],
        cyan: ['rose', 'indigo'],
        pink: ['blue', 'emerald'],
        lime: ['violet', 'indigo'],
        fuchsia: ['emerald', 'cyan'],
        sky: ['violet', 'amber'],
        purple: ['emerald', 'lime'],
        white: ['gray', 'blue'],
        red: ['blue', 'teal'],
        value: ['blue', 'purple']
      }
      return complementMap[color] ?? ['blue', 'indigo']
    }
  }
  
/**
 * Parse a hex color string to its `[r, g, b]` components (each 0–255).
 *
 * Single in-file home for the hex parse that `calculateBrightness`,
 * `getContrastTextColor`, and `deriveColor` previously each inlined verbatim
 * (A5/D2 — within-module dedup). Behaviour is intentionally identical to those
 * copies: `parseInt` on the byte substrings.
 *
 * NOTE on error handling (`r3455198479`): a malformed-but-string hex
 * (e.g. `#gggggg`, `#ff`) does NOT throw — `parseInt` returns `NaN`, so the
 * callers' `try/catch` does NOT fire and the `NaN` propagates (e.g.
 * `getContrastTextColor`/`deriveColor` can emit `#NaNNaNNaN`). Only non-string /
 * null input (where `.startsWith` throws) actually hits a caller's catch. This
 * is pre-existing behaviour preserved verbatim by the D2 no-op dedup and pinned
 * by a test; fixing the `NaN` propagation is a deliberate follow-up (it would
 * change rendered output, so it is out of D2's safe-no-op scope).
 *
 * It is deliberately NOT delegated to the canonical `utils/colors/hexToRgb`,
 * which returns `[0,0,0]` (not `NaN`) on an unparseable string and so would
 * change these callers' output. (The broader charts↔`utils/colors` color-math
 * convergence is deferred for the same reason — different formulas + edge
 * semantics make it a rendered-output change, not a no-op dedup; see the
 * changeset.)
 */
const hexToRgb255 = (hexColor: string): [number, number, number] => {
  const hex = hexColor.startsWith('#') ? hexColor.substring(1) : hexColor
  // Expand 3-digit shorthand (#abc → #aabbcc)
  const normalizedHex = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
  return [
    parseInt(normalizedHex.substring(0, 2), 16),
    parseInt(normalizedHex.substring(2, 4), 16),
    parseInt(normalizedHex.substring(4, 6), 16),
  ]
}

/**
 * Calculates the perceived brightness of a color based on its RGB components.
 * Uses the formula that accounts for human perception of different color channels.
 *
 * @param {string} hexColor - The hex color code to calculate brightness for
 * @returns {number} A value between 0 (darkest) and 1 (brightest)
 */
export const calculateBrightness = (hexColor: string): number => {
  try {
    // Parse RGB components (0–255) then normalise to 0–1
    const [r255, g255, b255] = hexToRgb255(hexColor)
    const r = r255 / 255
    const g = g255 / 255
    const b = b255 / 255

    // Calculate perceived brightness using the perceptual luminance formula
    // This weights green highest as humans are most sensitive to green light
    return (r * 0.299 + g * 0.587 + b * 0.114)
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('charts/colors: Error calculating brightness', e)
    }
    return 0.5 // Return middle brightness as fallback
  }
}

/**
 * Returns a theme-appropriate text color (slate shades) based on background brightness
 * Uses slate-800, slate-700, or slate-100 depending on background brightness
 * 
 * @param {string} backgroundColor - The hex color code of the background
 * @returns {string} A theme-appropriate text color for optimal contrast
 */
export const getThemeTextColor = (backgroundColor: string): string => {
  try {
    const brightness = calculateBrightness(backgroundColor)
    
    // Use theme-appropriate colors instead of stark black/white
    if (brightness > 0.65) {
      return '#1e293b' // slate-800 for light backgrounds
    } else if (brightness > 0.45) {
      return '#334155' // slate-700 for medium backgrounds
    } else {
      return '#f1f5f9' // slate-100 for dark backgrounds (off-white)
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('charts/colors: Error determining theme text color', e)
    }
    return '#f1f5f9' // Default to light text as safest option
  }
}

/**
 * Determines a complementary text color for a given background color
 * based on color contrast and readability guidelines. Uses the WCAG 2.0 guidelines
 * to ensure adequate contrast for accessibility.
 * 
 * @param {string} backgroundColor - The hex color code of the background
 * @returns {string} A complementary color that ensures readability
 */
export const getContrastTextColor = (backgroundColor: string): string => {
  try {
    // Convert hex to RGB (0–255)
    const [r, g, b] = hexToRgb255(backgroundColor)

    // Calculate relative luminance following WCAG 2.0 formula
    const toLinear = (val: number): number => {
      const normalized = val / 255
      return normalized <= 0.03928
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4)
    }
    
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
    
    // Generate a complementary color with good contrast
    if (luminance > 0.5) {
      // For light backgrounds, create a darker shade of the original color
      const darkenFactor = 0.6
      const newR = Math.max(0, Math.round(r * (1 - darkenFactor)))
      const newG = Math.max(0, Math.round(g * (1 - darkenFactor)))
      const newB = Math.max(0, Math.round(b * (1 - darkenFactor)))
      
      // Ensure the new color has sufficient contrast
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
    } else {
      // For dark backgrounds, create a lighter tint of the original color
      const lightenFactor = 0.7
      const newR = Math.min(255, Math.round(r + (255 - r) * lightenFactor))
      const newG = Math.min(255, Math.round(g + (255 - g) * lightenFactor))
      const newB = Math.min(255, Math.round(b + (255 - b) * lightenFactor))
      
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
    }
  } catch (e) {
    // Fallback to a neutral gray if any parsing errors
    if (process.env.NODE_ENV === 'development') {
      console.warn('charts/colors: Error determining contrast text color', e)
    }
    return '#757575'
  }
}

/**
 * Derives a new color from a base color by adjusting its brightness.
 * Ensures the derived color has good contrast for both text and background use.
 * 
 * @param {string} baseColor - The original hex color to derive from
 * @param {number} brightnessFactor - Amount to adjust brightness (0-1, higher values make lighter colors)
 * @returns {string} A new hex color derived from the base color
 */
export const deriveColor = (baseColor: string, brightnessFactor: number): string => {
  try {
    // Parse RGB (0–255)
    const [r, g, b] = hexToRgb255(baseColor)

    // Adjust brightness more dramatically for better contrast
    // Use a non-linear adjustment to create more distinct colors
    const adjustComponent = (component: number): number => {
      // Constrain brightness factor between 0-1
      const factor = Math.max(0, Math.min(1, brightnessFactor))
      
      // Non-linear adjustment that creates more separation
      // This helps with creating more distinguishable hierarchical colors
      const adjusted = component + (255 - component) * (factor * 0.8)
      
      // Ensure value is in valid RGB range
      return Math.min(255, Math.max(0, Math.round(adjusted)))
    }
    
    const newR = adjustComponent(r)
    const newG = adjustComponent(g)
    const newB = adjustComponent(b)
    
    // Convert back to hex
    const newHex = [newR, newG, newB]
      .map(c => c.toString(16).padStart(2, '0'))
      .join('')
    
    return `#${newHex}`
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('charts/colors: Error deriving color', e)
    }
    return baseColor // Return original color on error
  }
}